import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Send, Mic, Heart, BookmarkPlus, FileText, ThumbsUp, ThumbsDown, Copy, Share2, MoreHorizontal, LogOut, Loader2, X, AlertCircle, ChevronDown, Settings, PlusCircle, Info, XCircle, MessageSquare, Users, CheckCircle, ListTodo, Brain } from 'lucide-react';
import { useSearch, useLocation } from 'wouter';
import { useAuth, useUser } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';

import AppLayout from '../components/AppLayout';
import { getAllCoachProfiles, getCoachProfile } from '../lib/coachProfiles';
import { getCoachImage, hasCoachImage } from '../lib/coachImages';
import { useCoachingSessionStore } from '../lib/coachingSessionStore';
import {
  sendMessageToAI,
  saveChatMessage,
  removeSavedMessage,
  getChatSession,
  continueSession,
  updateChatSession,
  generateMoM,
  getUsageSummary,
  ApiError,
  API_BASE,
  updateSavedMessageMetadata,
  getSavedMessages,
  submitSessionFeedback,
  submitMessageFeedback,
} from '@/lib/api';
import { useWishlistStore } from '../lib/wishlistStore';
import type { SessionDetail } from '../types/session';
import { useToast } from '../hooks/useToast';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';
import CoachPersonaModal from '../components/CoachPersonaModal';
import CombinedCoachProfilePopover from '../components/CombinedCoachProfilePopover';
import { createCombinedCoachProfile } from '../lib/combinedCoachUtils';
import MessageActions from '../components/MessageActions';
import CoachPromptCard from '../components/CoachPromptCard';
import { parseCoachCTABlocks, extractFirstCTABlock } from '../lib/parseCoachCTA';
import PlanSummarySheet from '../components/PlanSummarySheet';
import SessionFeedbackModal from '../components/SessionFeedbackModal';
import { useCoachingProtocol, useCoachBehavior } from '../hooks/useCoachingProtocol';
import { useSessionMemoryStore } from '../lib/sessionMemoryStore';

type CoachBreakdown = {
  coach: string;
  text: string;
};

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  coachId?: string;
  timestamp: Date;
  liked?: boolean;
  saved?: boolean;
  thumbsUp?: boolean;
  thumbsDown?: boolean;
  source?: 'integrated' | 'coach';
  coachBreakdowns?: CoachBreakdown[];
  isError?: boolean;
  reactionCount?: {
    likes: number;
    thumbsUp: number;
    thumbsDown: number;
    saves: number;
  };
}

const LiveWaveform = ({ isRecording, stream }: { isRecording: boolean; stream: MediaStream | null }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isRecording || !stream || !canvasRef.current) {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      return;
    }

    let audioContext: AudioContext;
    try {
      audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch (e) {
      console.error('AudioContext not supported');
      return;
    }

    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 64;
    source.connect(analyser);

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      animationRef.current = requestAnimationFrame(draw);
      analyser.getByteFrequencyData(dataArray);

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const barWidth = (canvas.width / bufferLength) * 1.5;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        const barHeight = (dataArray[i] / 255) * canvas.height;
        ctx.fillStyle = '#4C62E3';
        ctx.fillRect(x, (canvas.height - barHeight) / 2, barWidth, barHeight);
        x += barWidth + 2;
      }
    };

    draw();

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (audioContext.state !== 'closed') audioContext.close();
    };
  }, [isRecording, stream]);

  return (
    <canvas
      ref={canvasRef}
      width={120}
      height={30}
      className="h-8 w-24 opacity-80"
    />
  );
};

const CoachingWorkspacePage = () => {
  const searchParams = useSearch();
  const [, navigate] = useLocation();
  const { getToken } = useAuth();
  const { user } = useUser();

  // Zustand store states
  const {
    selectedCoachIds,
    activeCoachId,
    sessionId,
    sessionTitle,
    messages,
    likedMessageIds,
    toggleCoach,
    setActiveCoachId,
    setSelectedCoachIds,
    setSessionData,
    setMessages,
    addMessage,
    addMessages,
    setLikedMessageIds,
    reset,
  } = useCoachingSessionStore();
  const MIN_ENGAGEMENT_MESSAGES = 5;
  const [inputMessage, setInputMessage] = useState('');
  const inputRef = useRef(inputMessage);
  
  // Executive Coaching Protocol Integration
  const coachingProtocol = useCoachingProtocol(sessionId);
  const [sessionOpeningShown, setSessionOpeningShown] = useState(false);
  const [engagementInterventionCount, setEngagementInterventionCount] = useState(0);

  // Sync a ref with inputMessage to avoid stale closures in recording callback
  useEffect(() => {
    inputRef.current = inputMessage;
  }, [inputMessage]);

  const [isTyping, setIsTyping] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isEndingSession, setIsEndingSession] = useState(false);
  const [showEndSessionSheet, setShowEndSessionSheet] = useState(false);
  const [isGeneratingMoM, setIsGeneratingMoM] = useState(false);
  const [showMoMPrompt, setShowMoMPrompt] = useState(false);
  const [showMoMFarewellPrompt, setShowMoMFarewellPrompt] = useState(false);
  const [momTriggerType, setMomTriggerType] = useState<'30-chat' | 'farewell' | 'manual' | null>(null);
  const [autoMoMInProgress, setAutoMoMInProgress] = useState(false);
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const [activeMobileCoachInfo, setActiveMobileCoachInfo] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [isPersonaModalOpen, setIsPersonaModalOpen] = useState(false);
  const [hasSeenBookmarkTip, setHasSeenBookmarkTip] = useState(false);
  const [aiMessageCount, setAiMessageCount] = useState(0);
  const [showLedgerCount, setShowLedgerCount] = useState(false);
  const [viewingCoach, setViewingCoach] = useState<any | null>(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptionError, setTranscriptionError] = useState<string | null>(null);
  const [recordingStream, setRecordingStream] = useState<MediaStream | null>(null);
  const [showSessionOptionsModal, setShowSessionOptionsModal] = useState(false);
  const [sessionOptions, setSessionOptions] = useState<any>(null);
  const [showMoMModal, setShowMoMModal] = useState(false);
  const [generatedMoMData, setGeneratedMoMData] = useState<any>(null);
  const FEATURE_CTA_FLOW = !!import.meta.env.VITE_FEATURE_CHAT_CTA_FLOW;
  const [handledCTAs, setHandledCTAs] = useState<Record<string, boolean>>({});
  const FEATURE_PHASE2 = !!import.meta.env.VITE_FEATURE_PHASE2_FLOW;
  const FEATURE_MESSAGE_FEEDBACK = !!import.meta.env.VITE_FEATURE_MESSAGE_FEEDBACK;
  const [isPlanOpen, setIsPlanOpen] = useState(false);
  const [planItems, setPlanItems] = useState<any[]>([]);
  const [isLoadingPlan, setIsLoadingPlan] = useState(false);
  const [isConfirmingPlan, setIsConfirmingPlan] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const recognitionRef = useRef<any>(null);
  const baselineVoiceTextRef = useRef('');

  // Toast system
  const { toasts, success, error, info, removeToast } = useToast();

  const { data: profile } = useCurrentUserProfile();

  // Refs
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Get coach profiles
  const coachProfiles = useMemo(() => getAllCoachProfiles(), []);
  const coachMap = useMemo(() => {
    const map: Record<string, any> = {};
    coachProfiles.forEach(coach => {
      map[coach.id] = coach;
    });
    return map;
  }, [coachProfiles]);

  // Selected coach profile
  const selectedCoachProfiles = useMemo(() => {
    return selectedCoachIds.map(id => coachProfiles.find(c => c.id === id)).filter(Boolean);
  }, [selectedCoachIds, coachProfiles]);

  // Check if at coach limit
  const isAtCoachLimit = useMemo(() => {
    return selectedCoachIds.length >= 3;
  }, [selectedCoachIds]);

  // Scroll to bottom of messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Derived state for engagement
  const userMessageCount = useMemo(() => {
    return messages.filter(m => m.sender === 'user').length;
  }, [messages]);

  const hasEnoughEngagement = userMessageCount >= MIN_ENGAGEMENT_MESSAGES;

  // Page exit detection for tab closing and back navigation
  useEffect(() => {
    // Handle tab/window closing
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Only prompt if there is enough engagement and MoM hasn't been generated
      if (hasEnoughEngagement) {
        setShowMoMPrompt(true);
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    // Handle back navigation and route changes
    const handlePopState = () => {
      if (hasEnoughEngagement) {
        // Show MoM prompt
        setShowMoMPrompt(true);
      }
    };

    // Handle clicks on links that might navigate away
    const handleLinkClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a');

      if (anchor && anchor.getAttribute('href') && anchor.getAttribute('href')?.startsWith('#') === false) {
        // Check if it's an external link or internal navigation
        const href = anchor.getAttribute('href');
        const isExternalLink = href?.startsWith('http') || href?.startsWith('mailto');
        const isSamePage = href === window.location.pathname;

        // If navigating away and there are messages
        if (!isExternalLink && !isSamePage && hasEnoughEngagement) {
          e.preventDefault();
          setShowMoMPrompt(true);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    document.addEventListener('click', handleLinkClick);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('click', handleLinkClick);
    };
  }, [hasEnoughEngagement]);

  const initialProcessedRef = useRef(false);

  // Load initial session data (saved IDs and initial context)
  useEffect(() => {
    const loadData = async () => {
      try {
        if (!sessionId) return;
        const token = await getToken();
        if (!token) return;

        // Load liked message IDs and session messages
        const detail = await getChatSession(sessionId, token);
        if (detail) {
          if (detail.mom_data) {
            setIsComplete(true);
          }
          if (Array.isArray(detail.savedMessageIds)) {
            setLikedMessageIds(detail.savedMessageIds);
          }
          if (Array.isArray(detail.messages) && detail.messages.length > 0 && messages.length === 0) {
            const mappedMessages: Message[] = detail.messages.map((m: any) => ({
              id: m.id || `msg_${Date.now()}_${Math.random()}`,
              text: m.text || m.content || '',
              sender: m.sender === 'user' ? 'user' : 'ai',
              coachId: m.coach || m.coachId,
              timestamp: m.timestamp ? new Date(m.timestamp) : new Date(),
              source: m.source,
              coachBreakdowns: m.coachBreakdowns,
            }));
            setMessages(mappedMessages);
          }

          // Sync selected coaches from backend session
          if (detail.coachTypes && Array.isArray(detail.coachTypes) && detail.coachTypes.length > 0) {
            const sessionCoachIds = detail.coachTypes;
            setSelectedCoachIds(sessionCoachIds);

            // Ensure active coach is set correctly
            if (!activeCoachId || !sessionCoachIds.includes(activeCoachId)) {
              setActiveCoachId(sessionCoachIds[0]);
            }
          }
        } else {
          localStorage.removeItem('hipo_last_coaching_session_id');
          if (messages.length === 0) {
            const errorMessage: Message = {
              id: `error_load_${Date.now()}`,
              text: "No active session found. Please start a new session.",
              sender: 'ai',
              timestamp: new Date(),
              isError: true,
            };
            setMessages([errorMessage]);
          }
        }

        // Check for initial message and context from URL
        if (!initialProcessedRef.current) {
          const urlParams = new URLSearchParams(window.location.search);
          let initialText = urlParams.get('initialMessage');
          const source = urlParams.get('source');
          const itemId = urlParams.get('itemId');
          const lastCoachId = urlParams.get('lastCoachId');

          if (source === 'email_reminder' && itemId && !initialText) {
            try {
              const tkn = await getToken();
              if (tkn) {
                const savedList = await getSavedMessages(tkn);
                const match = Array.isArray(savedList) ? savedList.find((m: any) => m.id === itemId) : null;
                if (match) {
                  initialText = `I want to discuss this action: ${match.messageContent}`;
                  if (match.coachType) {
                    const availableCoaches = Object.keys(coachMap);
                    if (availableCoaches.includes(match.coachType)) {
                      setSelectedCoachIds([match.coachType]);
                      setActiveCoachId(match.coachType);
                    }
                  }
                }
              }
            } catch (e) {
              console.warn('Email reminder prefill failed', e);
            }
          }

          // Decode initialMessage if it exists and looks encoded
          if (initialText) {
            try {
              // Check if it's already decoded or needs decoding
              if (initialText.includes('%')) {
                initialText = decodeURIComponent(initialText);
              }
            } catch (decodeError) {
              console.error('🔍 PHASE 2 DEBUG - Failed to decode initialMessage:', decodeError);
              // Keep original if decoding fails
            }
          }

          console.log('🔍 PHASE 1 DEBUG - CoachingWorkspacePage URL processing:', {
            currentURL: window.location.href,
            urlParams: Object.fromEntries(urlParams.entries()),
            initialText: initialText,
            initialTextLength: initialText ? initialText.length : 0,
            source: source,
            itemId: itemId,
            lastCoachId: lastCoachId,
            messagesLength: messages.length,
            isTyping: isTyping
          });

          // PHASE 4: Handle coach auto-selection from action ledger
          if (source === 'action_ledger' && lastCoachId) {
            console.log('🔍 PHASE 4 DEBUG - Processing coach auto-selection:', lastCoachId);

            // Verify the coach exists in available coaches
            const availableCoaches = Object.keys(coachMap);
            if (availableCoaches.includes(lastCoachId)) {
              console.log('🔍 PHASE 4 DEBUG - Auto-selecting coach:', lastCoachId);
              setSelectedCoachIds([lastCoachId]);
              setActiveCoachId(lastCoachId);
            } else {
              console.warn('🔍 PHASE 4 DEBUG - Coach not found, using default:', lastCoachId, 'Available:', availableCoaches);
            }
          }

          // Handle action ledger or regular initial message
          if (initialText && messages.length === 0 && !isTyping) {
            console.log('🔍 PHASE 1 DEBUG - Taking initialText path (SHOULD HAPPEN)');
            initialProcessedRef.current = true;
            setTimeout(() => {
              setInputMessage(initialText);
              // Auto-send if coming from action ledger
              if (source === 'action_ledger' && itemId) {
                console.log('🔍 PHASE 1 DEBUG - Auto-sending message for action_ledger');
                handleSendMessage();
              }
            }, 500);
          } else if (!initialText) {
            console.log('🔍 PHASE 1 DEBUG - Taking greeting path (SHOULD NOT HAPPEN for action ledger)');
            console.log('🔍 PHASE 1 DEBUG - Reason: initialText is falsy:', initialText);

            // PHASE 3: Skip greeting if coming from action_ledger (even without initialText)
            if (source === 'action_ledger') {
              console.log('🔍 PHASE 3 DEBUG - Skipping greeting for action_ledger source without initialText');
              initialProcessedRef.current = true;
              return; // Exit early, no greeting for action ledger
            }

            initialProcessedRef.current = true;

            // Fetch usage to see if it's the first session
            let totalSessions = 0;
            try {
              const usage = await getUsageSummary(token);
              totalSessions = usage.totalSessions || 0;
            } catch (usageErr) {
              console.warn('Failed to fetch usage summary for greeting:', usageErr);
            }

            // If it's a brand new session with no messages, add a greeting
            // PHASE 3: Additional safeguard - don't add greeting if action_ledger context exists
            if (messages.length === 0 && (!detail || !detail.messages || detail.messages.length === 0) && source !== 'action_ledger') {
              const isMultiCoach = selectedCoachIds.length > 1;
              const greetingPrefix = totalSessions > 0 ? "Welcome back!" : "Hi!";
              let dynamicGreeting = "";

              if (isMultiCoach) {
                // NEW INTEGRATED COACH MASTER SYSTEM PROMPT
                // Simple, direct opening - no framing, no interpretation
                dynamicGreeting = "Hi, I'm your Integrated Coach.\n\nWhat would you like to be coached on today?";
              } else {
                const profileObj = coachMap[selectedCoachIds[0] || activeCoachId || 'samuel'];
                
                if (profileObj?.openingStatement) {
                  dynamicGreeting = profileObj.openingStatement;
                } else {
                  const coachName = profileObj?.name || 'Coach';
                  const expertise = profileObj?.expertise || 'Mentor';
                  const coachIntro = `I am your ${expertise}. ${profileObj?.bio || ''}`.split('\n')[0];
                  
                  dynamicGreeting = `${greetingPrefix} I'm ${coachName}. ${coachIntro} How can I best support you today?`;
                }
              }

              const greeting: Message = {
                id: `greeting_${Date.now()}`,
                text: dynamicGreeting,
                sender: 'ai',
                coachId: isMultiCoach ? undefined : (selectedCoachIds[0] || activeCoachId || 'samuel'),
                source: isMultiCoach ? 'integrated' : undefined,
                timestamp: new Date(),
              };
              addMessage(greeting);
            }
          }
        }
      } catch (e) {
        console.warn('Unable to load session data', e);
      }
    };
    loadData();
  }, [sessionId, getToken]);

  // Update AI message count for third-message onboarding
  useEffect(() => {
    const aiMessages = messages.filter(m => m.sender === 'ai');
    setAiMessageCount(aiMessages.length);
  }, [messages]);

  // Check for farewell phrase in AI messages to trigger MoM
  useEffect(() => {
    if (messages.length === 0) return;

    const lastMessage = messages[messages.length - 1];
    if (lastMessage.sender === 'ai' && lastMessage.text) {
      const farewellPhrase = "Best of luck. Please generate the MoM for this discussion to save the summary of this chat.";
      if (lastMessage.text.includes(farewellPhrase)) {
        setMomTriggerType('farewell');
        setShowMoMFarewellPrompt(true);
      }
    }
  }, [messages]);

  // Effect to show ledger count after saving items
  useEffect(() => {
    if (likedMessageIds.length > 0) {
      setShowLedgerCount(true);
    }
  }, [likedMessageIds]);

  // Handle session option choices at 30-chat limit
  const handleSessionOption = useCallback((option: string) => {
    setShowSessionOptionsModal(false);
    setSessionOptions(null);

    switch (option) {
      case 'continueSession':
        // Actually extend the session via API
        handleContinueSession();
        break;
      case 'saveAndNew':
        // Generate MoM and create new session
        setMomTriggerType('manual');
        setShowMoMPrompt(true);
        break;
      case 'createMoM':
        // Just generate MoM without new session
        setMomTriggerType('30-chat');
        setShowMoMPrompt(true);
        break;
      default:
        console.warn('Unknown session option:', option);
    }
  }, [info, setMomTriggerType, setShowMoMPrompt]);

  // Handle continuing session by calling API to extend chat limit
  const handleContinueSession = useCallback(async () => {
    if (!sessionId) {
      error('Error', 'No active session to continue');
      return;
    }

    try {
      const token = await getToken();
      if (!token) {
        error('Error', 'Authentication required');
        return;
      }

      // Call the session continuation API
      const result = await continueSession(sessionId, token) as any;

      if (result?.status === 'extended') {
        success('Success', result.message || 'Session extended for 30 more exchanges');
        console.log(`Session ${sessionId} extended. New chat limit: ${result.newChatLimit}`);
      } else {
        error('Error', 'Failed to extend session');
      }
    } catch (err) {
      console.error('Error continuing session:', err);
      error('Error', err instanceof Error ? err.message : 'Failed to extend session');
    }
  }, [sessionId, getToken, success, error]);

  // Handle sending a message
  const handleSendMessage = useCallback(async () => {
    if (!inputMessage.trim() || isTyping) return;

    const newUserMessage: Message = {
      id: `user_${Date.now()}`,
      text: inputMessage,
      sender: 'user',
      timestamp: new Date(),
    };

    addMessage(newUserMessage);
    setInputMessage('');
    setIsTyping(true);

    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      if (!sessionId) throw new Error('No session ID');

      // If only one coach is selected, always target that coach
      // If multiple coaches are selected, send to all (null targetCoachId = multi-coach response)
      const targetCoach = selectedCoachIds.length === 1 ? selectedCoachIds[0] : null;

      const response = await sendMessageToAI(inputMessage, selectedCoachIds, sessionId, token, targetCoach);

      if (response && response.replies) {
        const aiMessages: Message[] = response.replies.map((reply: any) => {
          const messageId = reply.id || `ai_${Date.now()}_${Math.random()}`;
          return {
            id: messageId,
            text: reply.text || '',
            sender: 'ai',
            coachId: reply.coach || reply.coachId,
            timestamp: new Date(),
            source: reply.source,
            coachBreakdowns: reply.coachBreakdowns,
          };
        });
        addMessages(aiMessages);
      }
      setIsTyping(false);
    } catch (err) {
      setIsTyping(false);

      // Handle session limit (429) errors explicitly so the user sees a clear message in chat
      if (err instanceof ApiError && err.status === 429) {
        const errorData = err.data || {};
        const displayMessage = errorData.message || (err instanceof Error ? err.message : 'Session limit reached.');

        let finalMessage = displayMessage;
        if (errorData.reason === 'chat_limit_reached') {
          // New improved 30-chat limit handling with options
          setShowSessionOptionsModal(true);
          setSessionOptions(errorData.options);
          // Don't show the message in chat - show modal instead
          return;
        } else if (errorData.reason === 'chat_limit_auto_mom') {
          // Fallback for old auto-mom logic
          setMomTriggerType('30-chat');
          setShowMoMPrompt(true);
        }

        const systemErrorMessage: Message = {
          id: `error_${Date.now()}`,
          text: finalMessage,
          sender: 'ai',
          timestamp: new Date(),
          isError: true
        };
        addMessage(systemErrorMessage);
      } else {
        const errorMessage = err instanceof Error ? err.message : 'Failed to send message. Please try again.';
        error('Error', errorMessage);
      }
    }
  }, [inputMessage, isTyping, selectedCoachIds, coachMap, error, sessionId, getToken, addMessage, addMessages, setIsTyping]);

  // Handle coach switch
  const handleCoachSwitch = useCallback((coachId: string) => {
    setActiveCoachId(coachId);
  }, [setActiveCoachId]);

  // Start a new chat session
  const handleNewChat = useCallback(() => {
    reset();
    navigate('/coaching');
  }, [reset, navigate]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  const getPlaceholder = useCallback(() => {
    if (isComplete) return "Session completed - summary generated.";
    if (selectedCoachProfiles.length === 0) return "Select a coach to start...";
    if (selectedCoachProfiles.length === 1) {
      return `Ask ${selectedCoachProfiles[0]?.name} anything...`;
    }
    return "Ask your coach anything...";
  }, [selectedCoachProfiles, isComplete]);

  // Generate MoM with different trigger type handling
  const generateMoMAction = useCallback(async (triggerType?: '30-chat' | 'farewell' | 'manual') => {
    if (!sessionId) {
      error('No Session', 'Start or open a session first.');
      return;
    }
    setIsGeneratingMoM(true);
    setShowMoMPrompt(false);
    setShowMoMFarewellPrompt(false);
    setAutoMoMInProgress(true);

    const currentTrigger = triggerType || momTriggerType || 'manual';
    info('Generating', 'Creating Minutes of Meeting...');

    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      const momData = await generateMoM(sessionId, token);

      // Store MoM data and show modal instead of redirect
      setGeneratedMoMData(momData);
      setShowMoMModal(true);
      setIsComplete(true);

      // Clear last session ID so it doesn't appear as "Resume"
      localStorage.removeItem('hipo_last_coaching_session_id');

      success('Success', 'MoM generated successfully!');
    } catch (err) {
      console.error('Failed to generate MoM', err);

      // Handle session limit (429) or "too short" (400) errors explicitly
      if (err instanceof ApiError && (err.status === 429 || err.status === 400)) {
        const errorData = err.data || {};
        const displayMessage = err.message || errorData.message || 'Action required.';

        const systemErrorMessage: Message = {
          id: `error_mom_${Date.now()}`,
          text: displayMessage,
          sender: 'ai',
          timestamp: new Date(),
          isError: true
        };
        addMessage(systemErrorMessage);
      } else {
        error('Error', 'Failed to generate Minutes of Meeting.');
      }
    } finally {
      setIsGeneratingMoM(false);
      setAutoMoMInProgress(false);
      setMomTriggerType(null);
    }
  }, [sessionId, getToken, info, success, error, momTriggerType, reset, navigate]);

  // Wrapper for button onClick handlers
  const handleGenerateMoMClick = useCallback(() => {
    generateMoMAction('manual');
  }, [generateMoMAction]);

  // Dismiss MoM prompt
  const dismissMoMPrompt = useCallback(() => {
    setShowMoMPrompt(false);
    setMomTriggerType(null);
  }, []);

  // Handlers for different MoM triggers
  const handleAutoMoMConfirm = useCallback(() => {
    generateMoMAction(momTriggerType || '30-chat');
  }, [generateMoMAction, momTriggerType]);

  const handleFarewellMoMConfirm = useCallback(() => {
    generateMoMAction('farewell');
  }, [generateMoMAction]);

  // Cancel MoM and continue with user's intended action
  const cancelMoMAndContinue = useCallback(() => {
    setShowMoMPrompt(false);
    setMomTriggerType(null);
    // Allow user to continue with their intended navigation/action
  }, []);

  // Dismiss farewell MoM prompt and continue chatting
  const dismissFarewellPrompt = useCallback(() => {
    setShowMoMFarewellPrompt(false);
    setMomTriggerType(null);
  }, []);

  // MoM Modal handlers
  const handleContinueChat = useCallback(() => {
    setShowMoMModal(false);
    // Clear modal data but keep session active
  }, []);

  const handleGoToActions = useCallback(() => {
    setShowMoMModal(false);
    navigate('/wishlist?from=coaching&context=mom-generated' + (sessionId ? `&session=${sessionId}` : ''));
  }, [navigate, sessionId]);

  const handleViewInsights = useCallback(() => {
    setShowMoMModal(false);
    navigate('/insights?from=coaching&context=mom-generated' + (sessionId ? `&session=${sessionId}` : ''));
  }, [navigate, sessionId]);

  const handleCloseMoMModal = useCallback(() => {
    setShowMoMModal(false);
    setGeneratedMoMData(null);
  }, []);

  // Combined end session with MoM generation option
  const endSession = useCallback(async (generateMoMFirst = false) => {
    setIsEndingSession(true);

    try {
      if (generateMoMFirst) {
        setIsGeneratingMoM(true);
        info('Generating', 'Creating Minutes of Meeting before ending session...');

        try {
          const token = await getToken();
          if (!token) throw new Error('Not authenticated');
          if (!sessionId) throw new Error('No session ID available');
          await generateMoM(sessionId, token);
          success('Success', 'Minutes of Meeting generated successfully!');
          setIsComplete(true);

          // Small delay before resetting and redirecting
          await new Promise(resolve => setTimeout(resolve, 1500));
          reset();
        } catch (momErr) {
          console.error('Failed to generate MoM during session end:', momErr);
          error('Warning', 'Could not generate MoM, but session will be ended.');
        } finally {
          setIsGeneratingMoM(false);
        }
      }

      info('Ending Session', 'Wrapping up your coaching session...');
      // Simulate ending session
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Clear last session ID
      localStorage.removeItem('hipo_last_coaching_session_id');

      success('Session Ended', 'Your coaching session has been successfully concluded.');
      navigate('/coaching');
    } catch (err) {
      error('Error', 'Failed to end session. Please try again.');
      setIsEndingSession(false);
    }
  }, [sessionId, navigate, getToken, info, success, error]);

  const handleCoachToggle = useCallback(async (coachId: string) => {
    // Optimistically update the UI
    toggleCoach(coachId);

    // Persist to backend
    if (sessionId) {
      try {
        const token = await getToken();
        if (!token) return;

        // Calculate new coach IDs after toggle
        const isCurrentlySelected = selectedCoachIds.includes(coachId);
        const newCoachIds = isCurrentlySelected
          ? selectedCoachIds.filter(id => id !== coachId)
          : [...selectedCoachIds, coachId];

        // Update backend session
        await updateChatSession(sessionId, { coachTypes: newCoachIds }, token);
      } catch (err) {
        console.error('Failed to persist coach toggle:', err);
        // Revert optimistic update on error
        toggleCoach(coachId);
      }
    }
  }, [toggleCoach, sessionId, getToken, selectedCoachIds]);

  const handleSetActiveCoach = useCallback((coachId: string) => {
    if (!selectedCoachIds.includes(coachId)) return;
    setActiveCoachId(coachId);
  }, [selectedCoachIds, setActiveCoachId]);

  // Helper to ensure proper spacing when appending transcripts
  const appendTranscript = useCallback((baseline: string, transcript: string) => {
    const trimmedBaseline = baseline.trim();
    const trimmedTranscript = transcript.trim();
    if (!trimmedBaseline) return trimmedTranscript;
    if (!trimmedTranscript) return trimmedBaseline;
    return `${trimmedBaseline} ${trimmedTranscript}`;
  }, []);

  const transcribeAudio = useCallback(async (audioBlob: Blob) => {
    setIsTranscribing(true);
    setTranscriptionError(null);

    try {
      const token = await getToken();
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      const response = await fetch(`${API_BASE}/transcribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = data.message || data.error || `Transcription failed: ${response.statusText}`;
        throw new Error(errorMessage);
      }

      const transcribedText = data.text;

      if (transcribedText) {
        // Build the combined text using the accurate baseline capture
        setInputMessage(appendTranscript(baselineVoiceTextRef.current, transcribedText));
      }
    } catch (err) {
      console.error('Transcription failed:', err);
      const message = err instanceof Error ? err.message : 'Failed to transcribe audio. Please try typing instead.';
      setTranscriptionError(message);
      setTimeout(() => setTranscriptionError(null), 5000);
    } finally {
      setIsTranscribing(false);
    }
  }, [getToken, setInputMessage]);

  const slugify = useCallback((s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''), []);
  const endOfWeek = useCallback((d: Date) => {
    const date = new Date(d);
    const day = date.getDay();
    const diff = 7 - day;
    date.setDate(date.getDate() + diff);
    date.setHours(17, 0, 0, 0);
    return date;
  }, []);
  const isoLocal = useCallback((d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString(), []);
  const computeDueDate = useCallback((timeframe?: string) => {
    const tf = (timeframe || '').toLowerCase();
    const now = new Date();
    if (tf === 'today') {
      const d = new Date(now);
      d.setHours(17, 0, 0, 0);
      return isoLocal(d);
    }
    if (tf === 'this week') return isoLocal(endOfWeek(now));
    if (tf === 'next week') return isoLocal(endOfWeek(new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)));
    if (tf === 'in 2 weeks') return isoLocal(endOfWeek(new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000)));
    return undefined;
  }, [endOfWeek, isoLocal]);

  const [recentSave, setRecentSave] = useState<{ id: string; email: boolean; due: string | null } | null>(null);


  const openPlan = useCallback(async () => {
    if (!FEATURE_PHASE2) return;
    if (!sessionId) {
      info('Plan', 'No active session');
      return;
    }
    setIsPlanOpen(true);
    setIsLoadingPlan(true);
    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      const all = await getSavedMessages(token);
      const items = Array.isArray(all) ? all.filter((m: any) => m.sessionId === sessionId) : [];
      setPlanItems(items);
    } catch (e) {
      error('Error', 'Failed to load plan items.');
    } finally {
      setIsLoadingPlan(false);
    }
  }, [FEATURE_PHASE2, sessionId, getToken, error, info]);

  const handleConfirmPlan = useCallback(async () => {
    if (!FEATURE_PHASE2) return;
    setIsConfirmingPlan(true);
    try {
      await new Promise(r => setTimeout(r, 400));
      success('Plan Confirmed', 'Your focus areas are set.');
      setIsPlanOpen(false);
    } finally {
      setIsConfirmingPlan(false);
    }
  }, [FEATURE_PHASE2, success]);

  const handleSubmitFeedback = useCallback(async (score: number, comment?: string | null) => {
    if (!FEATURE_PHASE2) return;
    if (!sessionId) {
      error('No Session', 'Open a session first.');
      return;
    }
    setIsSubmittingFeedback(true);
    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      await submitSessionFeedback({ sessionId, npsScore: score, comment: comment ?? null }, token);
      success('Thanks', 'Feedback recorded.');
      setIsFeedbackOpen(false);
    } catch {
      error('Error', 'Failed to submit feedback.');
    } finally {
      setIsSubmittingFeedback(false);
    }
  }, [FEATURE_PHASE2, sessionId, getToken, success, error]);

  const startRecording = useCallback(async () => {
    try {
      // Capture the current input as the baseline from the ref to avoid stale state
      baselineVoiceTextRef.current = inputRef.current;

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setRecordingStream(stream);

      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/wav';
      const recorder = new MediaRecorder(stream, { mimeType });

      mediaRecorderRef.current = recorder;
      recordedChunksRef.current = [];

      // Real-time text preview using standard Web Speech API
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      if (SpeechRecognition) {
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';

        recognition.onresult = (event: any) => {
          let sessionTranscript = '';
          for (let i = 0; i < event.results.length; i++) {
            sessionTranscript += event.results[i][0].transcript;
          }

          if (sessionTranscript) {
            // Append real-time transcription to the baseline
            setInputMessage(appendTranscript(baselineVoiceTextRef.current, sessionTranscript));
          }
        };

        recognition.onerror = (e: any) => console.error('Speech recognition error', e);
        recognition.start();
        recognitionRef.current = recognition;
      }

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        const audioBlob = new Blob(recordedChunksRef.current, { type: mimeType });
        stream.getTracks().forEach(track => track.stop());
        setRecordingStream(null);
        await transcribeAudio(audioBlob);
      };

      recorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error('Failed to start recording:', err);
      setTranscriptionError('Microphone access denied');
      setTimeout(() => setTranscriptionError(null), 3000);
    }
  }, [transcribeAudio, setInputMessage]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }
      setIsRecording(false);
    }
  }, []);

  const handleRecordToggle = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  const headerTitle = useMemo(() => {
    if (sessionTitle) return sessionTitle;

    if (selectedCoachIds.length > 1) {
      const combinedProfile = createCombinedCoachProfile(selectedCoachIds);
      return combinedProfile?.name || 'Coaching Session';
    }

    if (selectedCoachIds.length === 1) {
      const coach = getCoachProfile(selectedCoachIds[0]);
      return `Session with ${coach?.name || 'Coach'}`;
    }

    return 'Coaching Workspace';
  }, [sessionTitle, selectedCoachIds]);

  const headerSubtitle = useMemo(() => {
    if (isComplete) {
      return 'Session Complete • Summary generated and saved to archive.';
    }

    if (selectedCoachIds.length > 1) {
      const coachNames = selectedCoachIds.map(id => getCoachProfile(id)?.name).filter(Boolean).join(', ');
      return `${coachNames} • Combined perspective`;
    }

    if (selectedCoachIds.length === 1) {
      const coach = getCoachProfile(selectedCoachIds[0]);
      return coach?.expertise || 'Personal coaching session';
    }

    return 'Curate your coaches and keep momentum with focused conversations.';
  }, [isComplete, selectedCoachIds]);

  return (
    <AppLayout currentPage="coaching">
      <div className="flex h-screen flex-col bg-white relative overflow-hidden">
        {/* MoM Generation Prompt */}
        {showMoMPrompt && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 sm:px-0">
            <div className="w-full max-w-md rounded-xl bg-white p-4 sm:p-6 shadow-2xl">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                  <AlertCircle className="h-5 w-5" />
                </div>
                <h2 className="font-display text-xl font-semibold text-slate-900">
                  {momTriggerType === '30-chat' ? 'Session Completed!' : 'Generate Session Summary?'}
                </h2>
              </div>
              <p className="mb-4 text-sm text-slate-600">
                {momTriggerType === '30-chat'
                  ? "You've reached the 30-chat limit. Generate MoM to save your session summary?"
                  : "It looks like you're ready to wrap up. Would you like to generate a Minutes of Meeting (MoM) summary? This should only be done at the end of a session."
                }
              </p>
              <p className="mb-6 text-sm font-medium text-amber-700">
                <strong>Important:</strong> Individual chat messages are not saved. Only MoMs are preserved for future reference and action tracking.
              </p>
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
                <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
                  <button
                    onClick={cancelMoMAndContinue}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    <XCircle className="h-4 w-4" />
                    Cancel & Continue
                  </button>
                  <button
                    onClick={dismissMoMPrompt}
                    className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
                  >
                    Remind Me Later
                  </button>
                </div>
                <button
                  onClick={momTriggerType === '30-chat' ? handleAutoMoMConfirm : handleGenerateMoMClick}
                  disabled={isGeneratingMoM}
                  className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-70"
                >
                  {isGeneratingMoM ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Generating...
                    </>
                  ) : (
                    <>
                      <FileText className="h-4 w-4" /> Generate MoM
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Farewell MoM Generation Prompt */}
        {showMoMFarewellPrompt && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 sm:px-0">
            <div className="w-full max-w-md rounded-xl bg-white p-4 sm:p-6 shadow-2xl">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                  <MessageSquare className="h-5 w-5" />
                </div>
                <h2 className="font-display text-xl font-semibold text-slate-900">Session Complete?</h2>
              </div>
              <p className="mb-4 text-sm text-slate-600">
                Your coach has indicated this discussion is finished. Generate MoM to save summary?
              </p>
              <p className="mb-6 text-sm font-medium text-blue-700">
                <strong>Note:</strong> You can continue chatting or save the session summary now.
              </p>
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
                <button
                  onClick={dismissFarewellPrompt}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  <MessageSquare className="h-4 w-4" />
                  Continue Chatting
                </button>
                <button
                  onClick={handleFarewellMoMConfirm}
                  disabled={isGeneratingMoM}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-70"
                >
                  {isGeneratingMoM ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Generating...
                    </>
                  ) : (
                    <>
                      <FileText className="h-4 w-4" /> Generate MoM
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <CoachPersonaModal
          isOpen={isPersonaModalOpen}
          onClose={() => setIsPersonaModalOpen(false)}
          coach={viewingCoach}
        />
        {/* Header */}
        <header className="flex-none border-b border-slate-100 bg-white px-3 py-3 sm:px-6 sm:py-5 z-10">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-1">
              <h1 className="font-display text-xl sm:text-2xl font-bold text-slate-900 leading-tight">{headerTitle}</h1>
              <p className="font-body text-xs sm:text-sm text-slate-600 truncate max-w-[280px] sm:max-w-none">{headerSubtitle}</p>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-3 pb-1 sm:pb-0">
              <button
                onClick={handleGenerateMoMClick}
                disabled={isGeneratingMoM || isComplete}
                className="whitespace-nowrap inline-flex items-center gap-1.5 sm:gap-2 rounded-xl bg-slate-50 border border-slate-200 px-2.5 py-2 sm:rounded-2xl sm:bg-[#EEF1FF] sm:border-transparent sm:px-4 sm:py-2.5 font-display text-xs sm:text-sm font-semibold text-slate-700 sm:text-[#1F3CCB] transition-colors hover:bg-slate-100 sm:hover:bg-[#DDE3FF] disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Close + New Chat"
              >
                {isGeneratingMoM ? (
                  <Loader2 className="h-4 w-4 animate-spin sm:text-[#1F3CCB]" strokeWidth={1.5} />
                ) : (
                  <FileText className="h-4 w-4 sm:text-[#1F3CCB]" strokeWidth={1.5} />
                )}
                <span className="sm:inline">Close + New Chat</span>
              </button>

              {/* Actions Button */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsActionsOpen(!isActionsOpen)}
                  className={`flex items-center justify-center rounded-xl px-2.5 py-2 sm:rounded-2xl border border-transparent transition-all duration-200 ${isActionsOpen ? 'bg-[#3B4CC5]' : 'bg-[#4C62E3]'} text-white shadow-sm hover:bg-[#3B4CC5] font-display text-xs sm:text-sm font-semibold gap-1.5 sm:gap-2 sm:px-4 sm:py-2.5`}
                  aria-label="Session actions"
                >
                  <Settings className={`h-4 w-4 text-white transition-transform duration-300 ${isActionsOpen ? 'rotate-90' : ''}`} />
                  <span className="hidden sm:inline text-white">Actions</span>
                </button>

                {isActionsOpen && (
                  <>
                    {/* Backdrop for mobile */}
                    <div
                      className="fixed inset-0 z-40 bg-black/25 backdrop-blur-[1px] sm:hidden"
                      onClick={() => setIsActionsOpen(false)}
                    />

                    {/* Menu Container: Bottom Sheet (Mobile) / Dropdown (Desktop) */}
                    <div className="fixed bottom-0 left-0 right-0 z-50 rounded-t-2xl bg-white p-4 shadow-[0_-4px_24px_rgba(0,0,0,0.1)] ring-1 ring-black/5 sm:absolute sm:top-full sm:bottom-auto sm:right-0 sm:left-auto sm:mt-2 sm:w-64 sm:rounded-xl sm:p-1 sm:shadow-lg origin-top-right transition-all duration-200 animate-in fade-in slide-in-from-top-2 sm:animate-none">
                      {/* Mobile Handle */}
                      <div className="mx-auto mb-6 h-1 w-12 rounded-full bg-slate-200 sm:hidden" />

                      <div className="flex flex-col gap-2 sm:gap-1">
                        <div className="px-2 pb-2 sm:hidden border-b border-slate-100 mb-2">
                          <h3 className="font-display text-lg font-semibold text-slate-900">Session Actions</h3>
                          <p className="text-sm text-slate-500">Manage your coaching session</p>
                        </div>

                        <button
                          onClick={handleNewChat}
                          className="flex w-full items-center gap-3 rounded-xl px-4 py-3 sm:text-left transition-colors hover:bg-slate-50 sm:gap-2 sm:rounded-lg sm:px-3 sm:py-2"
                        >
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-50 text-green-700 sm:h-auto sm:w-auto sm:bg-transparent sm:text-slate-500">
                            <PlusCircle className="h-5 w-5 sm:h-4 sm:w-4" strokeWidth={1.5} />
                          </div>
                          <div>
                            <span className="block font-medium text-slate-700 sm:text-sm text-base">New Chat</span>
                            <span className="block text-xs text-slate-500 sm:hidden">Start a fresh conversation</span>
                          </div>
                        </button>

                        <button
                          onClick={() => {
                            setIsDrawerOpen(true);
                            setIsActionsOpen(false);
                          }}
                          className="flex w-full items-center gap-3 rounded-xl px-4 py-3 sm:text-left transition-colors hover:bg-slate-50 sm:gap-2 sm:rounded-lg sm:px-3 sm:py-2"
                        >
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-[#4C62E3] sm:h-auto sm:w-auto sm:bg-transparent sm:text-slate-500">
                            <Users className="h-5 w-5 sm:h-4 sm:w-4" strokeWidth={1.5} />
                          </div>
                          <div>
                            <span className="block font-medium text-slate-700 sm:text-sm text-base">Manage Coaches</span>
                            <span className="block text-xs text-slate-500 sm:hidden">Add/remove coaches</span>
                          </div>
                        </button>

                        <div className="my-1 border-t border-slate-100" />

                        {FEATURE_PHASE2 && (
                          <button
                            onClick={() => {
                              openPlan();
                              setIsActionsOpen(false);
                            }}
                            className="flex w-full items-center gap-3 rounded-xl px-4 py-3 sm:text-left transition-colors hover:bg-slate-50 sm:gap-2 sm:rounded-lg sm:px-3 sm:py-2"
                          >
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-violet-50 text-[#4C62E3] sm:h-auto sm:w-auto sm:bg-transparent sm:text-slate-500">
                              <ListTodo className="h-5 w-5 sm:h-4 sm:w-4" strokeWidth={1.5} />
                            </div>
                            <div>
                              <span className="block font-medium text-slate-700 sm:text-sm text-base">Review Plan</span>
                              <span className="block text-xs text-slate-500 sm:hidden">Confirm saved focus areas</span>
                            </div>
                          </button>
                        )}

                        <button
                          onClick={() => {
                            setShowEndSessionSheet(true);
                            setIsActionsOpen(false);
                          }}
                          disabled={isEndingSession || isGeneratingMoM}
                          className="flex w-full items-center gap-3 rounded-xl px-4 py-3 sm:text-left transition-colors hover:bg-red-50 sm:gap-2 sm:rounded-lg sm:px-3 sm:py-2 disabled:opacity-50"
                        >
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-50 text-red-600 sm:h-auto sm:w-auto sm:bg-transparent sm:text-red-500">
                            <LogOut className="h-5 w-5 sm:h-4 sm:w-4" strokeWidth={1.5} />
                          </div>
                          <div>
                            <span className="block font-medium text-red-600 sm:text-sm text-base">End Session</span>
                            <span className="block text-xs text-red-400 sm:hidden">Save and exit</span>
                          </div>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>


          </div>
        </header>

        {/* End Session Dialog */}
        {showEndSessionSheet && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 sm:px-0">
            <div className="w-full max-w-md rounded-xl bg-white p-4 sm:p-6 shadow-2xl">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                  <LogOut className="h-5 w-5" />
                </div>
                <h2 className="font-display text-xl font-semibold text-slate-900">End Coaching Session?</h2>
              </div>
              <p className="mb-4 text-sm text-slate-600">
                Would you like to generate a Minutes of Meeting (MoM) summary before ending this session?
              </p>
              <p className="mb-6 text-sm font-medium text-amber-700">
                <strong>Important:</strong> Individual chat messages are not saved. Only MoMs are preserved for future reference.
              </p>
              <div className="flex flex-col gap-2 sm:flex-row sm:justify-end sm:gap-3">
                <button
                  onClick={() => setShowEndSessionSheet(false)}
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => endSession(false)}
                  disabled={isEndingSession}
                  className="rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-70"
                >
                  {isEndingSession ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Ending...
                    </>
                  ) : (
                    'End Without MoM'
                  )}
                </button>
                <button
                  onClick={() => endSession(true)}
                  disabled={isEndingSession || isGeneratingMoM}
                  className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-70"
                >
                  {isGeneratingMoM ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Generating MoM...
                    </>
                  ) : isEndingSession ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Ending...
                    </>
                  ) : (
                    <>
                      <FileText className="h-4 w-4" /> Generate MoM & End
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Conditionally render drawer content as a modal */}
        {isDrawerOpen && (
          <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50">
            <div className="w-full max-w-lg rounded-t-2xl bg-white p-6 shadow-2xl ring-1 ring-black/5 sm:rounded-xl">
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h2 className="font-display text-xl font-bold text-slate-900">Coaching Session</h2>
                  <p className="mt-1 font-body text-sm text-slate-500">
                    Select up to 3 coaches for this session
                  </p>
                </div>
                <button
                  onClick={() => setIsDrawerOpen(false)}
                  className="rounded-full bg-slate-100 p-2 text-slate-500 transition-colors hover:bg-slate-200"
                  title="Close"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="max-h-[75vh] space-y-3 overflow-y-auto pr-1 pb-20 sm:pb-0">
                {coachProfiles.map((coach) => {
                  const isSelected = selectedCoachIds.includes(coach.id);
                  const isActiveCoach = activeCoachId === coach.id;
                  const disableSelection = !isSelected && isAtCoachLimit;

                  return (
                    <div
                      key={coach.id}
                      className={`flex flex-col gap-4 rounded-3xl border px-4 py-4 transition-all duration-200 ${isSelected
                        ? 'border-[#4C62E3] bg-[#F4F6FF] shadow-sm'
                        : 'border-slate-200 bg-white hover:border-[#4C62E3]/50'
                        }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`relative flex h-14 w-14 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br ${coach.color} font-display text-xl font-semibold text-white`}>
                          {hasCoachImage(coach.id) ? (
                            <img
                              src={getCoachImage(coach.id)}
                              alt={coach.name}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            coach.name.charAt(0)
                          )}
                          {isActiveCoach && (
                            <span className="absolute -top-2 -right-2 rounded-full bg-[#4C62E3] px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">
                              Active
                            </span>
                          )}
                        </div>
                        <div className="flex-1 space-y-2">
                          <div>
                            <h3 className="font-display text-lg font-semibold text-slate-900">{coach.name}</h3>
                            <p className="font-display text-xs uppercase tracking-[0.22em] text-[#4C62E3]">
                              {coach.expertise}
                            </p>
                          </div>
                          <p className="font-body text-sm leading-relaxed text-slate-600">{coach.bio}</p>
                          <div className="flex flex-wrap gap-2 pt-1">
                            {(coach.specialties ?? []).slice(0, 3).map((tag) => (
                              <span
                                key={tag}
                                className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                        <button
                          onClick={() => handleCoachToggle(coach.id)}
                          disabled={disableSelection}
                          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-colors ${isSelected
                            ? 'border-[#4C62E3] bg-[#4C62E3] text-white'
                            : disableSelection
                              ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400'
                              : 'border-slate-300 bg-white text-slate-400 hover:border-[#4C62E3] hover:text-[#4C62E3]'
                            }`}
                        >
                          {isSelected ? (
                            <span className="text-lg font-semibold">-</span>
                          ) : (
                            <span className="text-lg font-semibold">+</span>
                          )}
                        </button>
                      </div>
                      <button
                        onClick={() => handleSetActiveCoach(coach.id)}
                        disabled={!isSelected}
                        className={`w-full rounded-xl border px-4 py-2 text-sm font-semibold transition-colors ${isSelected
                          ? isActiveCoach
                            ? 'border-[#4C62E3] bg-[#4C62E3] text-white'
                            : 'border-[#4C62E3] bg-white text-[#4C62E3] hover:bg-[#F4F6FF]'
                          : 'border-slate-200 text-slate-600 hover:border-[#4C62E3] hover:text-[#4C62E3]'
                          }`}
                      >
                        {isActiveCoach ? 'Currently Active' : 'Make Active Coach'}
                      </button>
                    </div>
                  );
                })}
              </div>
              <div className="fixed bottom-0 left-0 right-0 border-t border-slate-100 bg-white p-4 sm:static sm:mt-6 sm:border-0 sm:bg-transparent sm:p-0">
                <button
                  onClick={() => setIsDrawerOpen(false)}
                  type="button"
                  className="inline-flex w-full items-center justify-center rounded-xl bg-[#4C62E3] px-5 py-3.5 font-display text-base font-semibold text-white shadow-lg shadow-blue-200 transition-all hover:bg-[#3B4CC5] hover:shadow-blue-300 active:scale-[0.98] sm:py-2.5 sm:text-sm sm:shadow-sm"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Coach Switcher - Enhanced with Add/Remove (Hidden on mobile) */}
        <div className="hidden sm:block mt-4 w-full overflow-x-auto md:overflow-visible no-scrollbar">
          <div className="flex gap-2 pb-2 min-w-max md:min-w-0">
            {coachProfiles.map((coach) => {
              const isSelected = selectedCoachIds.includes(coach.id);
              const isActive = activeCoachId === coach.id;
              const canAdd = !isSelected && selectedCoachIds.length < 3;
              const canRemove = isSelected && selectedCoachIds.length > 1;

              return (
                <div key={coach.id} className="relative group flex-shrink-0">
                  <button
                    onClick={() => {
                      if (!isSelected && canAdd) {
                        // If not selected and can add, toggle to add
                        handleCoachToggle(coach.id);
                      }
                      // If already selected, clicking does nothing (they're all active)
                    }}
                    disabled={!isSelected && !canAdd}
                    className={`flex flex-col items-center rounded-xl px-2 sm:px-3 py-2 transition-all duration-200 ${isSelected
                      ? 'bg-[#4C62E3]/10 ring-2 ring-[#4C62E3]'
                      : canAdd
                        ? 'opacity-40 hover:opacity-70 hover:bg-slate-50'
                        : 'opacity-20 cursor-not-allowed'
                      }`}
                    title={
                      isSelected
                        ? `Switch to ${coach.name}`
                        : canAdd
                          ? `Add ${coach.name}`
                          : 'Maximum 3 coaches'
                    }
                    aria-label={
                      isSelected
                        ? `Switch to ${coach.name}`
                        : canAdd
                          ? `Add ${coach.name}`
                          : 'Maximum 3 coaches reached'
                    }
                  >
                    <div className={`relative h-8 w-8 sm:h-10 sm:w-10 rounded-full overflow-hidden border-2 ${isSelected
                      ? 'border-[#4C62E3]'
                      : 'border-slate-200'
                      }`}>
                      {hasCoachImage(coach.id) ? (
                        <img src={getCoachImage(coach.id)} alt={coach.name} className="h-full w-full object-cover" />
                      ) : (
                        <div className={`flex h-full w-full items-center justify-center bg-gradient-to-br ${coach.color} text-white font-semibold text-sm`}>
                          {coach.name.charAt(0)}
                        </div>
                      )}
                      {isSelected && <div className="absolute inset-0 bg-[#4C62E3]/10 ring-2 ring-[#4C62E3] rounded-full"></div>}

                      {/* Add icon for unselected coaches */}
                      {!isSelected && canAdd && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                          <PlusCircle size={16} className="text-white" />
                        </div>
                      )}
                    </div>
                    <p className={`hidden sm:block mt-1 text-xs font-medium truncate max-w-[60px] sm:max-w-none ${isSelected
                      ? 'text-[#4C62E3]'
                      : 'text-slate-400'
                      }`}>
                      {coach.name}
                    </p>
                  </button>

                  {/* Remove button for selected coaches (except if only one left) */}
                  {isSelected && canRemove && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCoachToggle(coach.id);
                      }}
                      className="absolute -top-1 -right-1 h-6 w-6 sm:h-5 sm:w-5 rounded-full bg-red-500 text-white flex items-center justify-center md:opacity-0 md:group-hover:opacity-100 opacity-100 transition-opacity hover:bg-red-600 shadow-md z-20"
                      title={`Remove ${coach.name}`}
                      aria-label={`Remove ${coach.name}`}
                    >
                      <X size={12} />
                    </button>
                  )}

                  {/* Coach Details Tooltip - Hidden on mobile, visible on hover for desktop */}
                  <div className="hidden sm:block absolute left-1/2 top-full z-10 mt-2 w-64 -translate-x-1/4 transform rounded-xl bg-white p-3 shadow-lg ring-1 ring-black ring-opacity-5 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    <div className="flex items-start gap-3">
                      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br ${coach.color} text-white`}>
                        {coach.name.charAt(0)}
                      </div>
                      <div>
                        <h3 className="font-display text-sm font-semibold text-slate-900">{coach.name}</h3>
                        <p className="text-xs font-medium text-[#4C62E3] uppercase tracking-wider">{coach.expertise}</p>
                        <p className="mt-1 text-xs text-slate-600">{coach.bio}</p>
                      </div>
                    </div>
                  </div>

                  {/* Mobile Coach Info - Tap to show modal */}
                  <div className="sm:hidden">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveMobileCoachInfo(coach.id === activeMobileCoachInfo ? null : coach.id);
                      }}
                      className="absolute -top-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-white text-[#4C62E3] border border-[#4C62E3]/20 shadow-sm z-10"
                      aria-label={`Show info about ${coach.name}`}
                    >
                      <Info size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile Coach Info Modal */}
        {
          activeMobileCoachInfo && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 sm:hidden">
              <div className="w-full max-w-xs rounded-xl bg-white p-4 shadow-xl">
                {(() => {
                  const coach = coachProfiles.find(c => c.id === activeMobileCoachInfo);
                  if (!coach) return null;

                  return (
                    <>
                      <div className="flex items-start gap-3">
                        <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br ${coach.color} text-white`}>
                          {hasCoachImage(coach.id) ? (
                            <img src={getCoachImage(coach.id)} alt={coach.name} className="h-full w-full object-cover" />
                          ) : coach.name.charAt(0)}
                        </div>
                        <div>
                          <h3 className="font-display text-lg font-semibold text-slate-900">{coach.name}</h3>
                          <p className="text-xs font-medium text-[#4C62E3] uppercase tracking-wider">{coach.expertise}</p>
                        </div>
                      </div>
                      <p className="mt-3 text-sm text-slate-600">{coach.bio}</p>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {(coach.specialties ?? []).map((tag: string) => (
                          <span key={tag} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <button
                        onClick={() => setActiveMobileCoachInfo(null)}
                        className="mt-4 w-full rounded-lg bg-slate-100 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200"
                      >
                        Close
                      </button>
                    </>
                  );
                })()}
              </div>
            </div>
          )
        }

        {/* Main Chat Area */}
        <main className="flex-1 min-h-0 flex flex-col relative">
          <section className="flex-1 overflow-y-auto px-2 py-4 sm:px-6 sm:py-6 relative">
            <div className="absolute inset-0 overflow-y-auto scrollbar-thin-theme">
              {/* Action Ledger Counter */}
              {showLedgerCount && likedMessageIds.length > 0 && (
                <div className="absolute top-4 right-6 flex items-center gap-2 rounded-full bg-[#4C62E3]/5 px-3 py-1.5 text-sm text-[#1F3CCB] transition-all duration-200 hover:bg-[#4C62E3]/10">
                  <BookmarkPlus className="h-4 w-4" />
                  <span className="font-medium">{likedMessageIds.length} items in Action Ledger</span>
                </div>
              )}
              <div className="mx-auto flex h-full max-w-4xl flex-col gap-4 sm:gap-6">
                {messages.length === 0 && (
                  <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-6 py-8 text-center">
                    <p className="font-display text-base font-semibold text-slate-700">Start the conversation with your coach</p>
                    <p className="font-body text-sm text-slate-500">
                      Ask about priorities, roadblocks, or upcoming decisions to trigger a high-signal response.
                    </p>
                  </div>
                )}

                {/* Messages */}
                {messages.map((message, index) => {
                  const aiMessageIndex = messages.slice(0, index + 1).filter(m => m.sender === 'ai').length;
                  const isThirdMessage = aiMessageIndex === 3;

                  return (
                    <div key={message.id} className="flex gap-2 sm:gap-4 group">
                      <div className="flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center overflow-hidden rounded-full border-2 border-white/80 bg-white shadow-sm">
                        {message.sender === 'user' ? (
                          user?.imageUrl ? (
                            <img
                              src={user.imageUrl}
                              alt={user?.firstName || 'User'}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <span className="font-display text-sm font-semibold text-slate-700">
                              {user?.firstName?.charAt(0) || 'U'}
                            </span>
                          )
                        ) : (
                          message.coachId && hasCoachImage(message.coachId) ? (
                            <img
                              src={getCoachImage(message.coachId)}
                              alt={coachMap[message.coachId]?.name || 'Coach'}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <span className="font-display text-sm font-semibold text-slate-700">
                              {message.source === 'integrated' ? '∑' : coachMap[message.coachId || '']?.name?.charAt(0) || 'C'}
                            </span>
                          )
                        )}
                      </div>
                      <div className={`relative max-w-[85%] sm:max-w-[75%] rounded-3xl px-3.5 py-2.5 sm:px-5 sm:py-4 ${message.isError
                        ? 'bg-red-50 border border-red-200 text-red-900'
                        : message.sender === 'user'
                          ? 'bg-[#4C62E3] text-white'
                          : message.source === 'integrated'
                            ? 'bg-[#F4F6FF] border border-[#4C62E3]/40 text-slate-900'
                            : 'bg-slate-100 text-slate-900'
                        }`}>
                        {message.isError && (
                          <div className="flex items-center gap-2 mb-1 text-red-600">
                            <AlertCircle className="h-4 w-4" />
                            <span className="text-xs font-medium">Error</span>
                          </div>
                        )}
                        {message.sender !== 'user' && !message.isError && (
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-xs font-bold text-slate-600">
                              {message.source === 'integrated'
                                ? (createCombinedCoachProfile(selectedCoachIds)?.name || 'Integrated Perspective')
                                : (coachMap[message.coachId || activeCoachId || 'samuel']?.name || 'Coach')}
                            </p>
                            {message.source !== 'integrated' && (
                              <button onClick={() => {
                                setViewingCoach(coachMap[message.coachId || activeCoachId || 'samuel']);
                                setIsPersonaModalOpen(true);
                              }} className="text-slate-400 hover:text-slate-600" title="View coach persona">
                                <Info size={14} />
                              </button>
                            )}
                            {message.source === 'integrated' && selectedCoachIds.length > 1 && (() => {
                              const combinedCoach = createCombinedCoachProfile(selectedCoachIds);
                              return combinedCoach ? (
                                <CombinedCoachProfilePopover combinedCoach={combinedCoach}>
                                  <button className="text-slate-400 hover:text-slate-600" title="View combined coach persona">
                                    <Info size={14} />
                                  </button>
                                </CombinedCoachProfilePopover>
                              ) : null;
                            })()}
                          </div>
                        )}
                        <p className={`whitespace-pre-wrap ${message.sender === 'user' ? 'text-white' : ''}`}>{message.text}</p>
                        {FEATURE_CTA_FLOW && message.sender !== 'user' && !message.isError && (() => {
                          const cta = extractFirstCTABlock(message.text || '');
                          if (!cta) return null;
                          const labelOrText = cta.fields.label || cta.fields.text || '';
                          const key = `${message.id}:${cta.type}:${slugify(labelOrText)}`;
                          if (handledCTAs[key]) return null;

                          const handleSave = async (payload: Record<string, string>) => {
                            try {
                              if (!sessionId) {
                                error('No Session', 'Start or open a session first.');
                                return;
                              }
                              const token = await getToken();
                              if (!token) {
                                error('Auth Error', 'Please sign in to save.');
                                return;
                              }
                              const coachType = message.coachId || (selectedCoachIds[0] || 'samuel');
                              const baseId = slugify(payload.label || payload.text || labelOrText || 'item');
                              const msgId = `cta_${sessionId}_${baseId}`;

                              const isSchedule = cta.type === 'schedule';
                              const isInsight = cta.type === 'insight';
                              const chosenLabel = (payload.label || cta.fields.label || labelOrText || '').trim();
                              const contentString = isSchedule
                                ? (chosenLabel || 'Next coaching connect')
                                : isInsight
                                  ? (payload.text || chosenLabel || 'Insight')
                                  : (chosenLabel.startsWith('FOCUS AREA:') ? chosenLabel : `FOCUS AREA: ${chosenLabel}`);

                              const saved = await saveChatMessage(
                                sessionId,
                                msgId,
                                contentString,
                                coachType,
                                token
                              );
                              const dbId = saved?.id;
                              const patchNeeded = !!(payload.priority || payload.status || payload.timeframe || payload.metric);
                              if (dbId && patchNeeded) {
                                const updates: any = {
                                  priority: payload.priority,
                                  status: payload.status,
                                  timeframe: payload.timeframe,
                                  dueDate: computeDueDate(payload.timeframe),
                                  successMetric: payload.metric || undefined,
                                };
                                await updateSavedMessageMetadata({ id: dbId, ...updates }, token);
                              }
                              if (dbId) {
                                const initDue = computeDueDate(payload.timeframe) || null;
                                setRecentSave({ id: dbId, email: true, due: initDue });
                              }
                              success('Saved', 'Added to your Action Ledger');
                              setShowLedgerCount(true);
                              setHandledCTAs({ ...handledCTAs, [key]: true });
                            } catch (e) {
                              console.error('CTA save failed', e);
                              error('Save Failed', 'Unable to save.');
                            }
                          };

                          const handleSkip = () => setHandledCTAs({ ...handledCTAs, [key]: true });

                          const cardType = (cta.type === 'schedule' ? 'schedule' : (cta.type === 'insight' ? 'insight' : 'action')) as any;
                          return (
                            <CoachPromptCard
                              type={cardType}
                              fields={cta.fields}
                              onSave={handleSave}
                              onSkip={handleSkip}
                            />
                          );
                        })()}
                      </div>
                      {message.sender !== 'user' && (
                        <MessageActions
                          message={message}
                          isBookmarked={likedMessageIds.includes(message.id)}
                          isThirdMessage={isThirdMessage}
                          hasSeenBookmarkTip={hasSeenBookmarkTip}
                          onBookmark={async () => {
                            try {
                              if (!sessionId) {
                                error('No Session', 'Unable to save message.');
                                return;
                              }
                              const token = await getToken();
                              if (!token) {
                                error('Auth Error', 'Please sign in to save messages.');
                                return;
                              }
                              const isLiked = likedMessageIds.includes(message.id);
                              if (isLiked) {
                                await removeSavedMessage(sessionId, message.id, token);
                                setLikedMessageIds(likedMessageIds.filter((id) => id !== message.id));
                              } else {
                                const coachType = message.coachId || (selectedCoachIds[0] || 'samuel');
                                await saveChatMessage(sessionId, message.id, message.text, coachType, token);
                                setLikedMessageIds([...likedMessageIds, message.id]);
                                setHasSeenBookmarkTip(true);
                                setShowLedgerCount(true);
                              }
                            } catch (e) {
                              console.error('Toggle bookmark failed', e);
                              error('Save Failed', 'Unable to update saved messages.');
                            }
                          }}
                          onFeedback={async (type) => {
                            try {
                              if (!FEATURE_MESSAGE_FEEDBACK) return;
                              if (!sessionId) return;
                              const token = await getToken();
                              if (!token) return;
                              const coachType = message.coachId || (selectedCoachIds[0] || 'samuel');
                              await submitMessageFeedback(
                                sessionId,
                                message.id,
                                coachType,
                                type === 'helpful' ? 'helpful' : 'not_helpful',
                                token,
                                {
                                  messageSource: (message.source as any) || 'coach',
                                  messageIndex: index,
                                  deviceType: (typeof window !== 'undefined' && /mobile|iphone|android/i.test(navigator.userAgent)) ? 'mobile' : 'desktop',
                                  clientTs: Date.now(),
                                }
                              );
                            } catch (e) {
                              console.warn('Message feedback submission failed (non-fatal):', e);
                            }
                          }}
                        />
                      )}
                    </div>
                  );
                })}

                {/* Typing indicator */}
                {isTyping && (
                  <div className="flex gap-4">
                    <div className="flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center overflow-hidden rounded-full border-2 border-white/80 bg-slate-200">
                      <span className="font-display text-sm font-semibold text-slate-700">...</span>
                    </div>
                    <div className="max-w-[85%] sm:max-w-[75%] rounded-3xl bg-slate-100 px-3.5 py-2.5 sm:px-5 sm:py-4 text-slate-900">
                      <div className="flex gap-1">
                        <span className="h-2 w-2 animate-bounce rounded-full bg-slate-400"></span>
                        <span className="h-2 w-2 animate-bounce rounded-full bg-slate-400 animation-delay-150"></span>
                        <span className="h-2 w-2 animate-bounce rounded-full bg-slate-400 animation-delay-300"></span>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </div>
          </section>

          {/* Input Area */}
          <div className="border-t border-slate-200 bg-white p-2 sm:p-4 safe-area-bottom">
            {recentSave && (
              <div className="mb-2 flex flex-col gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-xs font-display font-semibold text-slate-700">Added to Action Ledger</div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-xs text-slate-700 border border-slate-200">
                    <input
                      type="checkbox"
                      checked={recentSave.email}
                      onChange={async (e) => {
                        try {
                          const tkn = await getToken();
                          if (!tkn) return;
                          await updateSavedMessageMetadata({ id: recentSave.id, emailReminders: e.target.checked }, tkn);
                          setRecentSave({ ...recentSave, email: e.target.checked });
                        } catch {}
                      }}
                    />
                    Email follow-ups
                  </label>
                  <div className="flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-xs text-slate-700 border border-slate-200">
                    <span>Due</span>
                    <input
                      type="date"
                      defaultValue={recentSave.due ? recentSave.due.split('T')[0] : ''}
                      onChange={async (e) => {
                        try {
                          const tkn = await getToken();
                          if (!tkn) return;
                          const v = e.target.value || '';
                          await updateSavedMessageMetadata({ id: recentSave.id, dueDate: v || undefined }, tkn);
                          setRecentSave({ ...recentSave, due: v ? `${v}T00:00:00` : null });
                        } catch {}
                      }}
                      aria-label="Due date"
                      title="Due date"
                      className="rounded border border-slate-200 px-2 py-1 text-xs"
                    />
                  </div>
                  <button
                    onClick={() => setRecentSave(null)}
                    className="rounded-lg px-3 py-2 text-xs font-semibold text-slate-600 hover:text-slate-800"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}
            <div className="relative flex items-end gap-2 sm:gap-3">
              <div className="relative flex-1">
                <textarea
                  ref={textareaRef}
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={getPlaceholder()}
                  rows={1}
                  disabled={isComplete || isTranscribing}
                  className="w-full resize-none rounded-2xl border border-slate-200 px-4 py-3 text-sm sm:text-base placeholder-slate-400 focus:border-royal-blue focus:outline-none focus:ring-2 focus:ring-royal-blue/20 disabled:bg-slate-50 disabled:text-slate-400"
                />
              </div>
              <button
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || isTyping || isComplete || isTranscribing}
                className={`rounded-full p-3 transition-all duration-200 ${!inputMessage.trim() || isTyping || isComplete || isTranscribing
                  ? 'text-slate-600 bg-slate-100 cursor-not-allowed'
                  : 'bg-royal-blue text-white hover:bg-slate-800 shadow-sm'
                  }`}
                aria-label="Send message"
                title="Send message"
              >
                <Send className="h-4 w-4" />
              </button>
              {isTranscribing && (
                <div className="absolute -top-10 left-0 flex items-center gap-2 rounded-full bg-royal-blue/10 px-3 py-1 text-[10px] font-bold text-royal-blue shadow-sm z-[60]">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Finishing transcription...
                </div>
              )}
              {isRecording && (
                <div className="absolute -top-14 left-0 flex items-center gap-3 rounded-2xl bg-white p-2 shadow-lg border border-slate-100 z-[60]">
                  <LiveWaveform isRecording={isRecording} stream={recordingStream} />
                  <div className="flex items-center gap-1.5 px-2">
                    <div className="h-2 w-2 animate-pulse rounded-full bg-green-500" />
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Live</span>
                  </div>
                </div>
              )}
              {transcriptionError && (
                <div className="absolute -top-10 left-0 flex items-center gap-2 rounded-full bg-red-100 px-3 py-1 text-[10px] font-bold text-red-600 shadow-sm z-[60]">
                  <AlertCircle className="h-3 w-3" />
                  {transcriptionError}
                </div>
              )}
              <button
                onClick={handleRecordToggle}
                disabled={isComplete || isTranscribing}
                className={`rounded-full p-3 transition-colors ${isRecording ? 'bg-green-500 text-white shadow-lg shadow-green-200' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  } ${isComplete || isTranscribing ? 'opacity-50 cursor-not-allowed' : ''}`}
                aria-label={isRecording ? "Stop recording" : "Start recording"}
                title={isRecording ? "Stop recording" : "Start recording"}
              >
                <div className="relative">
                  <Mic className={`h-5 w-5 ${isRecording ? 'text-white' : ''}`} />
                  {isRecording && (
                    <div className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-white animate-ping" />
                  )}
                </div>
              </button>
            </div>
          </div>
        </main >

        {/* Session Options Modal */}
        {showSessionOptionsModal && sessionOptions && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="mx-4 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
              <div className="mb-6 text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Session Complete!</h3>
                <p className="mt-2 text-sm text-gray-600">
                  You've completed 30 coaching exchanges. What would you like to do next?
                </p>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => handleSessionOption('continueSession')}
                  className="w-full rounded-lg bg-blue-600 px-4 py-3 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
                >
                  {sessionOptions.continueSession}
                </button>
                <button
                  onClick={() => handleSessionOption('saveAndNew')}
                  className="w-full rounded-lg bg-slate-100 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-200 transition-colors"
                >
                  {sessionOptions.saveAndNew}
                </button>
                <button
                  onClick={() => handleSessionOption('createMoM')}
                  className="w-full rounded-lg bg-green-100 px-4 py-3 text-sm font-medium text-green-700 hover:bg-green-200 transition-colors"
                >
                  {sessionOptions.createMoM}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* MoM Success Modal */}
        {showMoMModal && generatedMoMData && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="mx-4 w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
              <div className="mb-6 text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Minutes of Meeting Generated!</h3>
                <p className="mt-2 text-sm text-gray-600">
                  Your session summary has been created and saved. What would you like to do next?
                </p>
              </div>
              <div className="space-y-3">
                <button
                  onClick={handleContinueChat}
                  className="w-full rounded-lg bg-[#4C62E3] px-4 py-3 text-sm font-medium hover:bg-[#4C62E3]/90 transition-colors"
                >
                  <div className="flex items-center justify-center gap-2 text-white">
                    <MessageSquare className="h-4 w-4" />
                    Continue Coaching Conversation
                  </div>
                </button>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={handleGoToActions}
                    className="rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-center gap-2">
                      <ListTodo className="h-4 w-4" />
                      Action Ledger
                    </div>
                  </button>
                  <button
                    onClick={handleViewInsights}
                    className="rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-center gap-2">
                      <Brain className="h-4 w-4" />
                      View Insights
                    </div>
                  </button>
                </div>
                {FEATURE_PHASE2 && (
                  <button
                    onClick={() => setIsFeedbackOpen(true)}
                    className="w-full rounded-lg bg-slate-100 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-200 transition-colors"
                  >
                    Rate Session
                  </button>
                )}
                <button
                  onClick={handleCloseMoMModal}
                  className="w-full text-center text-sm text-gray-500 hover:text-gray-700 transition-colors mt-4"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        {FEATURE_PHASE2 && (
          <>
            <PlanSummarySheet
              isOpen={isPlanOpen}
              items={planItems}
              isLoading={isLoadingPlan}
              isConfirming={isConfirmingPlan}
              onClose={() => setIsPlanOpen(false)}
              onConfirm={handleConfirmPlan}
              onGenerateMoM={() => {
                setIsPlanOpen(false);
                handleGenerateMoMClick();
              }}
            />
            <SessionFeedbackModal
              isOpen={isFeedbackOpen}
              onClose={() => setIsFeedbackOpen(false)}
              onSubmit={handleSubmitFeedback}
              isSubmitting={isSubmittingFeedback}
            />
          </>
        )}
      </div >
    </AppLayout >
  );
};

export default CoachingWorkspacePage;
